/**
 * Performance monitoring utility for iframe communication
 * Tracks message volume and provides statistics in development mode
 */

interface MessageStats {
  type: string;
  count: number;
  lastSent: number;
}

class IframePerformanceMonitor {
  private stats: Map<string, MessageStats> = new Map();
  private totalMessages = 0;
  private startTime = Date.now();
  private isEnabled = false;

  constructor() {
    // Only enable in development mode
    this.isEnabled = import.meta.env.DEV;
    
    if (this.isEnabled) {
      this.setupPeriodicReporting();
    }
  }

  /**
   * Track a message being sent
   */
  public trackMessage(type: string): void {
    if (!this.isEnabled) return;

    const existing = this.stats.get(type);
    const now = Date.now();

    if (existing) {
      existing.count++;
      existing.lastSent = now;
    } else {
      this.stats.set(type, {
        type,
        count: 1,
        lastSent: now,
      });
    }

    this.totalMessages++;
  }

  /**
   * Get statistics for all message types
   */
  public getStats() {
    const duration = (Date.now() - this.startTime) / 1000;
    const messagesPerSecond = this.totalMessages / duration;

    const byType = Array.from(this.stats.values()).sort((a, b) => b.count - a.count);

    return {
      totalMessages: this.totalMessages,
      duration: duration.toFixed(2),
      messagesPerSecond: messagesPerSecond.toFixed(2),
      byType,
    };
  }

  /**
   * Print statistics to console
   */
  public printStats(): void {
    if (!this.isEnabled) return;

    const stats = this.getStats();
    
    console.log('Iframe Communication Stats', {
      totalMessages: stats.totalMessages,
      duration: stats.duration,
      messagesPerSecond: stats.messagesPerSecond,
      byType: stats.byType
    });
  }

  /**
   * Reset all statistics
   */
  public reset(): void {
    this.stats.clear();
    this.totalMessages = 0;
    this.startTime = Date.now();
  }

  /**
   * Setup periodic reporting in development
   */
  private setupPeriodicReporting(): void {
    // Report stats every 30 seconds in development
    setInterval(() => {
      if (this.totalMessages > 0) {
        this.printStats();
      }
    }, 30000);
  }
}

// Create singleton instance
export const performanceMonitor = new IframePerformanceMonitor();

// Expose to window for manual debugging
if (import.meta.env.DEV) {
  (window as any).iframePerformance = {
    getStats: () => performanceMonitor.getStats(),
    printStats: () => performanceMonitor.printStats(),
    reset: () => performanceMonitor.reset(),
  };
}
